import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { of, timer, EMPTY } from 'rxjs';
import { map, catchError, switchMap, tap, withLatestFrom, takeUntil, ignoreElements, delay, exhaustMap, filter } from 'rxjs/operators';
import { StripeActions } from './stripe.actions';
import { StripeApiService } from '@app/core/api/stripe';
import { LoggerService, ExternalNavigationService, ToastNotificationService } from '@app/core/infrastructure';
import { PaymentService } from '@app/core/infrastructure/payment';
import { StripeSessionTracker } from '@app/core/infrastructure/stripe';
import { selectIsAuthenticated } from './stripe.selectors';
import { AppState } from '../index';
import { UserActions } from '@app/store/user/user.actions';

@Injectable()
export class StripeEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store<AppState>);
  private readonly stripeApiService = inject(StripeApiService);
  private readonly sessionTracker = inject(StripeSessionTracker);
  private readonly externalNav = inject(ExternalNavigationService);
  private readonly toastService = inject(ToastNotificationService);
  private readonly paymentService = inject(PaymentService);
  private readonly router = inject(Router);
  private readonly logger = inject(LoggerService).scope('StripeEffects');
  
  // Load All Plans (combined endpoint - preferred)
  loadAllPlans$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StripeActions.loadAllPlans),
      switchMap(() =>
        this.stripeApiService.getAllPlans().pipe(
          map(response => StripeActions.loadAllPlansSuccess({ response })),
          catchError(error => {
            this.logger.error('Load all plans failed:', error);
            return of(StripeActions.loadAllPlansFailure({ error: error.message }));
          })
        )
      )
    )
  );

  // Load User Subscription
  // Uses exhaustMap to ignore duplicate load requests while one is in progress
  // This prevents race conditions when multiple components request load simultaneously
  loadUserSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StripeActions.loadUserSubscription, StripeActions.autoRefreshTick),
      withLatestFrom(this.store.select(selectIsAuthenticated)),
      exhaustMap(([action, isAuthenticated]) => {
        if (!isAuthenticated) {
          this.logger.debug('Not authenticated, skipping subscription load');
          return EMPTY;
        }

        this.logger.debug('Loading user subscription');
        return this.stripeApiService.getUserSubscription().pipe(
          map(subscription => StripeActions.loadUserSubscriptionSuccess({ subscription })),
          catchError(error => {
            // Stop auto-refresh and suppress error on 401 (unauthorized) or 404/500 (user deleted)
            // These are expected after account deletion when requests may still be in flight
            if (error.status === 401 || error.status === 404 || error.status === 500) {
              this.logger.debug(`Subscription load received ${error.status}, stopping auto-refresh (likely account deleted or logged out)`);
              this.store.dispatch(StripeActions.stopAutoRefresh());
              this.store.dispatch(StripeActions.setAuthenticationState({ isAuthenticated: false }));
              // Return empty to suppress error - this is expected behavior during/after account deletion
              return EMPTY;
            }
            
            this.logger.error('Load user subscription failed:', error);
            return of(StripeActions.loadUserSubscriptionFailure({ error: error.message }));
          })
        );
      })
    )
  );

  // Auto-refresh subscription every 60 seconds
  autoRefresh$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StripeActions.startAutoRefresh),
      switchMap(() =>
        timer(0, 60000).pipe(
          map(() => StripeActions.autoRefreshTick()),
          takeUntil(this.actions$.pipe(ofType(StripeActions.stopAutoRefresh)))
        )
      )
    )
  );

  // Stop auto-refresh when user logs out
  stopAutoRefreshOnLogout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StripeActions.setAuthenticationState),
      switchMap(({ isAuthenticated }) => {
        if (!isAuthenticated) {
          return of(StripeActions.stopAutoRefresh(), StripeActions.clearCache());
        }
        return EMPTY;
      })
    )
  );

  // Stop auto-refresh immediately when account deletion starts
  // This prevents subscription requests from being sent after the user is deleted
  stopAutoRefreshOnAccountDeletion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.deleteAccount),
      tap(() => {
        this.logger.debug('Account deletion started - stopping stripe auto-refresh');
      }),
      switchMap(() => of(
        StripeActions.stopAutoRefresh(),
        StripeActions.setAuthenticationState({ isAuthenticated: false }),
        StripeActions.clearCache()
      ))
    )
  );

  // Create Subscription Checkout
  createSubscriptionCheckout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StripeActions.createSubscriptionCheckout),
      switchMap(({ priceId, promoCode }) =>
        this.stripeApiService.createSubscriptionCheckout(priceId, promoCode).pipe(
          map(response => StripeActions.createSubscriptionCheckoutSuccess({ response })),
          catchError(error => {
            // 409: User has an active Apple subscription — must cancel it first
            if (error.status === 409) {
              const conflictMsg = error.error?.message || 'You have an active Apple subscription. Please cancel it in the App Store before subscribing via Stripe.';
              this.toastService.showError(conflictMsg);
              return of(StripeActions.createSubscriptionCheckoutFailure({ error: conflictMsg }));
            }
            this.logger.error('Create subscription checkout failed:', error);
            return of(StripeActions.createSubscriptionCheckoutFailure({ error: error.message }));
          })
        )
      )
    )
  );

  // Redirect to checkout on success with platform-aware browser handling
  redirectToCheckout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StripeActions.createSubscriptionCheckoutSuccess, StripeActions.createSecondsCheckoutSuccess),
      switchMap(({ response }) => {
        // Reviewer bypass: backend returns mock data, don't navigate
        if (response.session_id === 'reviewer_bypass') {
          this.logger.info('Reviewer bypass detected — skipping checkout navigation');
          this.toastService.showInfo('Your plan is already fully active');
          return of(StripeActions.externalNavigationComplete({ context: 'checkout' }));
        }
        return this.externalNav.navigate(response.checkout_url, {
          toolbarColor: '#FF6532',
          analyticsEvent: 'stripe_checkout_opened'
        }).pipe(
          // Keep loading state for 1.5s to prevent button re-enable during navigation
          // This prevents accidental double-clicks while browser is opening (native)
          // or page is redirecting (web)
          delay(1500),
          // Dispatch complete action to clear loading state after delay
          // Note: In production/staging with Universal Links, appUrlOpen will catch the redirect
          // In development without Universal Links, users will manually close the browser
          // The browserFinished event is informational only and doesn't need special handling
          // The actual success/cancel flow is handled by the pricing page component
          // via query parameters when the universal link redirects back to the app
          map(() => StripeActions.externalNavigationComplete({ context: 'checkout' })),
          catchError((error) => {
            // Error: dispatch failure action
            return of(StripeActions.externalNavigationFailed({
              context: 'checkout',
              url: response.checkout_url,
              error: error.message || 'Failed to open checkout'
            }));
          })
        );
      })
    )
  );

  // Create Seconds Checkout (uses price_id)
  createSecondsCheckout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StripeActions.createSecondsCheckout),
      switchMap(({ priceId }) =>
        this.stripeApiService.createSecondsCheckout(priceId).pipe(
          map(response => StripeActions.createSecondsCheckoutSuccess({ response })),
          catchError(error => {
            // 409: User has an active Apple subscription — Stripe checkout blocked
            if (error.status === 409) {
              const conflictMsg = error.error?.message || 'You have an active Apple subscription. Please cancel it in the App Store before purchasing via Stripe.';
              this.toastService.showError(conflictMsg);
              return of(StripeActions.createSecondsCheckoutFailure({ error: conflictMsg }));
            }
            this.logger.error('Create seconds checkout failed:', error);
            return of(StripeActions.createSecondsCheckoutFailure({ error: error.message }));
          })
        )
      )
    )
  );

  // Get Customer Portal
  getCustomerPortal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StripeActions.getCustomerPortal),
      switchMap(({ targetPriceId }) =>
        this.stripeApiService.getCustomerPortal(targetPriceId).pipe(
          map(response => StripeActions.getCustomerPortalSuccess({ response })),
          catchError(error => {
            this.logger.error('Get customer portal failed:', error);
            return of(StripeActions.getCustomerPortalFailure({ error: error.message }));
          })
        )
      )
    )
  );

  // Redirect to portal on success
  redirectToPortal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StripeActions.getCustomerPortalSuccess),
      switchMap(({ response }) => {
        // Reviewer bypass: backend returns mock data, don't navigate
        if (response.session_id === 'reviewer_bypass') {
          this.logger.info('Reviewer bypass detected — skipping portal navigation');
          this.toastService.showInfo('Your plan is already fully active');
          return of(StripeActions.externalNavigationComplete({ context: 'portal' }));
        }
        return this.externalNav.navigate(response.portal_url, {
          toolbarColor: '#FF6532',
          analyticsEvent: 'stripe_portal_opened'
        }).pipe(
          // Keep loading state for 1.5s to prevent button re-enable during navigation
          // This prevents accidental double-clicks while browser is opening (native)
          // or page is redirecting (web)
          delay(1500),
          // Dispatch complete action to clear loading state after delay
          // Note: Portal redirect works similarly to checkout
          // The actual return flow is handled by the app's deep link configuration
          map(() => StripeActions.externalNavigationComplete({ context: 'portal' })),
          catchError((error) => {
            return of(StripeActions.externalNavigationFailed({
              context: 'portal',
              url: response.portal_url,
              error: error.message || 'Failed to open portal'
            }));
          })
        );
      })
    )
  );

  // Check Session Availability
  checkSessionAvailability$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StripeActions.checkSessionAvailability),
      switchMap(() =>
        this.stripeApiService.checkSessionAvailability().pipe(
          map(response => StripeActions.checkSessionAvailabilitySuccess({ response })),
          catchError(error => {
            this.logger.error('Check session availability failed:', error);
            return of(StripeActions.checkSessionAvailabilityFailure({ error: error.message }));
          })
        )
      )
    )
  );

  // Session Tracking - Start
  startSessionTracking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StripeActions.startSessionTracking),
      tap(({ initialRemainingSeconds, initialMultiplier }) => {
        this.sessionTracker.startSessionTracking(initialRemainingSeconds, initialMultiplier);
      }),
      switchMap(() =>
        this.sessionTracker.sessionUsage$.pipe(
          map(seconds => StripeActions.updateSessionUsage({ seconds })),
          takeUntil(this.actions$.pipe(ofType(StripeActions.stopSessionTracking)))
        )
      )
    )
  );

  // Session Tracking - Update Multiplier Mid-Session
  setSessionTrackerMultiplier$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StripeActions.setSessionTrackerMultiplier),
      tap(({ multiplier }) => {
        this.sessionTracker.setMultiplier(multiplier);
      })
    ),
    { dispatch: false }
  );

  // Session Tracking - Stop
  // Refreshes subscription after stopping to ensure UI shows updated remaining minutes
  // Only refreshes if user is still authenticated (skips during logout/account deletion)
  // Uses direct API call to bypass exhaustMap in loadUserSubscription$ which could
  // silently drop the refresh if an auto-refresh request is already in-flight
  stopSessionTracking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StripeActions.stopSessionTracking),
      withLatestFrom(this.store.select(selectIsAuthenticated)),
      tap(([, isAuthenticated]) => {
        this.sessionTracker.stopSessionTracking();
        if (isAuthenticated) {
          this.logger.debug('Session stopped - refreshing subscription to update remaining minutes');
        }
      }),
      filter(([, isAuthenticated]) => isAuthenticated),
      delay(500),
      switchMap(() =>
        this.stripeApiService.getUserSubscription().pipe(
          map(subscription => StripeActions.loadUserSubscriptionSuccess({ subscription })),
          catchError(error => {
            this.logger.error('Failed to refresh subscription after session stop:', error);
            return of(StripeActions.loadUserSubscriptionFailure({ error: error.message }));
          })
        )
      )
    )
  );

  // Activate Subscription (End Trial Early)
  activateSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StripeActions.activateSubscription),
      switchMap(() =>
        this.stripeApiService.activateSubscription().pipe(
          map(response => StripeActions.activateSubscriptionSuccess({ response })),
          catchError(error => {
            this.logger.error('Activate subscription failed:', error);
            return of(StripeActions.activateSubscriptionFailure({ error: error.message }));
          })
        )
      )
    )
  );

  // Refresh subscription after successful activation
  // Uses delay to ensure activation state is processed before refresh
  // Directly calls API instead of dispatching action to avoid exhaustMap blocking
  refreshAfterActivation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StripeActions.activateSubscriptionSuccess),
      tap(({ response }) => {
        this.logger.info('Subscription activated successfully, refreshing subscription data', response);
      }),
      delay(500), // Small delay to ensure backend has processed the activation
      switchMap(() =>
        this.stripeApiService.getUserSubscription().pipe(
          map(subscription => StripeActions.loadUserSubscriptionSuccess({ subscription })),
          catchError(error => {
            this.logger.error('Failed to refresh subscription after activation:', error);
            return of(StripeActions.loadUserSubscriptionFailure({ error: error.message }));
          })
        )
      )
    )
  );

  // ==================== IAP (In-App Purchase) Effects ====================

  // Load IAP products from the native store
  iapLoadProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StripeActions.iapLoadProducts),
      switchMap(() =>
        this.paymentService.loadProducts().pipe(
          map(products => StripeActions.iapLoadProductsSuccess({ products })),
          catchError(error => {
            this.logger.error('IAP load products failed:', error);
            return of(StripeActions.iapLoadProductsFailure({ error: error.message || 'Failed to load products' }));
          })
        )
      )
    )
  );

  // IAP Purchase — triggers native purchase sheet
  iapPurchase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StripeActions.iapPurchase),
      switchMap(({ productId }) =>
        this.paymentService.purchase(productId).pipe(
          map(result => {
            if (result.success) {
              return StripeActions.iapPurchaseSuccess({ result });
            }
            // User cancelled — don't treat as error
            if (result.error === 'cancelled') {
              return StripeActions.iapPurchaseFailure({ error: 'cancelled' });
            }
            // 409 conflict from receipt validation — user has active Stripe subscription
            if (result.error?.includes('409') || result.error?.includes('Stripe')) {
              this.toastService.showError(result.error || 'You have an active Stripe subscription. Please cancel it before purchasing via Apple.');
            }
            return StripeActions.iapPurchaseFailure({ error: result.error || 'Purchase failed' });
          }),
          catchError(error => {
            this.logger.error('IAP purchase failed:', error);
            return of(StripeActions.iapPurchaseFailure({ error: error.message || 'Purchase failed' }));
          })
        )
      )
    )
  );

  // Refresh subscription after successful IAP purchase
  // Backend has already processed the receipt at this point (the success emission
  // now comes from validateReceiptWithBackend, not .finished()). A small delay
  // handles any backend replication lag.
  refreshAfterIapPurchase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StripeActions.iapPurchaseSuccess),
      tap(({ result }) => {
        this.logger.info('IAP purchase successful, refreshing subscription', result);
      }),
      delay(500), // Reduced from 1s — backend already validated the receipt
      switchMap(() =>
        this.stripeApiService.getUserSubscription().pipe(
          tap(() => {
            // Navigate to avatars page to trigger onboarding flow
            // (welcome video + speaker identification popups)
            this.router.navigate(['/avatars'], {
              queryParams: { purchase: 'success', source: 'iap' },
              replaceUrl: true
            });
          }),
          map(subscription => StripeActions.loadUserSubscriptionSuccess({ subscription })),
          catchError(error => {
            this.logger.error('Failed to refresh subscription after IAP purchase:', error);
            return of(StripeActions.loadUserSubscriptionFailure({ error: error.message }));
          })
        )
      )
    )
  );

  // Safety-net: second subscription refresh 3s after IAP purchase
  // Catches edge cases where the first refresh returned slightly stale data
  // due to backend replication lag.
  refreshAfterIapPurchaseFollowUp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StripeActions.iapPurchaseSuccess),
      delay(3000),
      switchMap(() =>
        this.stripeApiService.getUserSubscription().pipe(
          map(subscription => StripeActions.loadUserSubscriptionSuccess({ subscription })),
          catchError(error => {
            this.logger.error('Follow-up subscription refresh after IAP purchase failed:', error);
            return EMPTY;
          })
        )
      )
    )
  );

  // IAP Restore Purchases
  iapRestore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StripeActions.iapRestore),
      switchMap(() =>
        this.paymentService.restorePurchases().pipe(
          map(result => {
            if (result.success) {
              this.toastService.showSuccess('Purchases restored successfully');
              return StripeActions.iapRestoreSuccess({ result });
            }
            return StripeActions.iapRestoreFailure({ error: result.error || 'Restore failed' });
          }),
          catchError(error => {
            this.logger.error('IAP restore failed:', error);
            return of(StripeActions.iapRestoreFailure({ error: error.message || 'Restore failed' }));
          })
        )
      )
    )
  );

  // Refresh subscription after successful restore
  refreshAfterIapRestore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StripeActions.iapRestoreSuccess),
      delay(500),
      switchMap(() =>
        this.stripeApiService.getUserSubscription().pipe(
          map(subscription => StripeActions.loadUserSubscriptionSuccess({ subscription })),
          catchError(error => of(StripeActions.loadUserSubscriptionFailure({ error: error.message })))
        )
      )
    )
  );
}
